import { Select, Typography } from 'antd';
import type { DefaultOptionType } from 'antd/es/select';

export type FarmPickerOption = {
  value: string | number;
  label: string;
  subtitle?: string;
  searchText?: string;
};

type FarmPickerFieldProps = {
  disabled?: boolean;
  label: string;
  loading?: boolean;
  onChange?: (value: string | number) => void;
  options: FarmPickerOption[];
  placeholder?: string;
  value?: string | number;
  /** When true, always show read-only text (no dropdown). */
  readOnly?: boolean;
};

const renderOptionLabel = (option: FarmPickerOption) => (
  <div style={{ padding: '2px 0' }}>
    <div style={{ fontWeight: 600, color: '#101828', lineHeight: 1.35 }}>{option.label}</div>
    {option.subtitle ? (
      <div style={{ fontSize: 12, color: '#667085', lineHeight: 1.35, marginTop: 2 }}>
        {option.subtitle}
      </div>
    ) : null}
  </div>
);

export function FarmPickerField({
  disabled,
  label,
  loading,
  onChange,
  options,
  placeholder,
  value,
  readOnly = false,
}: FarmPickerFieldProps) {
  if (!options.length && !loading) {
    return null;
  }

  const normalizedValue = value === undefined || value === null ? '' : String(value);
  const selectedOption = options.find((option) => String(option.value) === normalizedValue);
  const showAsText = readOnly || (options.length <= 1 && !loading);

  const selectOptions: DefaultOptionType[] = options.map((option) => ({
    value: String(option.value),
    label: option.label,
    searchText: option.searchText || [option.label, option.subtitle].filter(Boolean).join(' '),
    subtitle: option.subtitle,
  }));

  return (
    <div style={{ textAlign: 'left' }}>
      <Typography.Text
        style={{
          display: 'block',
          fontSize: 11,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: 0.9,
          color: '#98a2b3',
          marginBottom: 6,
        }}
      >
        {label}
      </Typography.Text>
      {showAsText ? (
        <div>
          <Typography.Text
            style={{
              display: 'block',
              fontSize: 16,
              lineHeight: 1.3,
              fontWeight: 700,
              color: '#101828',
              wordBreak: 'break-word',
            }}
          >
            {selectedOption?.label || placeholder || ''}
          </Typography.Text>
          {selectedOption?.subtitle ? (
            <Typography.Text
              style={{
                display: 'block',
                marginTop: 4,
                fontSize: 13,
                lineHeight: 1.4,
                color: '#667085',
              }}
            >
              {selectedOption.subtitle}
            </Typography.Text>
          ) : null}
        </div>
      ) : (
        <Select
          aria-label={label}
          disabled={disabled || loading}
          loading={loading}
          onChange={onChange}
          optionFilterProp="searchText"
          optionLabelProp="label"
          options={selectOptions}
          placeholder={placeholder}
          showSearch
          size="large"
          value={normalizedValue || undefined}
          style={{ width: '100%' }}
          optionRender={(option) => {
            const farmOption = options.find((item) => String(item.value) === String(option.value));

            if (!farmOption) {
              return option.label;
            }

            return renderOptionLabel(farmOption);
          }}
        />
      )}
    </div>
  );
}
